import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Modal = ({ project, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const autoplayRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (modalRef.current) modalRef.current.focus();
    return () => {
      document.body.style.overflow = 'unset';
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, []);

  useEffect(() => {
    if (project.type === 'image' && project.images && project.images.length > 1 && isAutoplay) {
      autoplayRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === project.images.length - 1 ? 0 : prevIndex + 1
        );
      }, 3000);
    }
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [project, isAutoplay, currentImageIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && project.type === 'image') navigateImage('prev');
      else if (e.key === 'ArrowRight' && project.type === 'image') navigateImage('next');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [project, onClose]);

  const navigateImage = (direction) => {
    if (!project.images || project.images.length <= 1) return;
    setIsAutoplay(false);
    if (direction === 'next')
      setCurrentImageIndex((prevIndex) =>
        prevIndex === project.images.length - 1 ? 0 : prevIndex + 1
      );
    else
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? project.images.length - 1 : prevIndex - 1
      );
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Modal untuk ${project.title}`}
    >
      <div
        ref={modalRef}
        className="relative max-w-6xl max-h-[90vh] w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden animate-scale-up"
        tabIndex="-1"
      >
        {/* Tombol tutup */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
          aria-label="Tutup modal"
        >
          <X size={20} />
        </button>

        {/* Konten utama */}
        <div className="flex flex-col h-full">
          <div className="flex-1 relative">
            {project.type === 'image' ? (
              <div className="relative h-96 md:h-[500px] lg:h-[600px]">
                <img
                  src={project.images ? project.images[currentImageIndex] : project.thumbnail}
                  alt={`${project.title} - Gambar ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />

                {project.images && project.images.length > 1 && (
                  <>
                    <button
                      onClick={() => navigateImage('prev')}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                      <ChevronLeft size={24} />
                    </button>

                    <button
                      onClick={() => navigateImage('next')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                      <ChevronRight size={24} />
                    </button>

                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {project.images.length}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="relative">
                {/* ✅ YouTube video pakai kontrol bawaan */}
                <iframe
                  src={`${project.video}?autoplay=1&controls=1&modestbranding=1&rel=0&enablejsapi=1`}
                  title={project.title}
                  allow="autoplay; fullscreen; encrypted-media"
                  allowFullScreen
                  className="w-full h-96 md:h-[500px] lg:h-[600px] rounded-xl"
                />
              </div>
            )}
          </div>

          {/* Info project */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {project.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 capitalize">
                  {project.category}
                </p>
              </div>
            </div>

            {project.description && (
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {project.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
